import { ArrowDown, ArrowUp, ArrowUpDown, ListFilter } from 'lucide-react';
import { useState } from 'react';

import { cn } from '../lib/utils';
import type { DataTableColumn } from './data-table-types';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { TableHead } from './ui/table';

const SORT_ICONS = { asc: ArrowUp, desc: ArrowDown } as const;
const ARIA_SORT = { asc: 'ascending', desc: 'descending' } as const;

interface DataTableHeaderCellProps<TRow> {
  column: DataTableColumn<TRow>;
}

export function DataTableHeaderCell<TRow>({ column }: DataTableHeaderCellProps<TRow>) {
  const { sort, filter } = column;
  const [open, setOpen] = useState(false);

  const alignment = cn(
    column.align === 'right' && 'text-right',
    column.align === 'center' && 'text-center',
    column.className,
  );

  // A header with neither keeps today's markup, so tables that opt into nothing look the same.
  if (!sort && !filter) return <TableHead className={alignment}>{column.header}</TableHead>;

  const SortIcon = sort?.direction ? SORT_ICONS[sort.direction] : ArrowUpDown;

  return (
    <TableHead
      className={alignment}
      aria-sort={sort?.direction ? ARIA_SORT[sort.direction] : undefined}
    >
      <div
        className={cn(
          'flex items-center gap-1 whitespace-nowrap',
          column.align === 'right' && 'justify-end',
          column.align === 'center' && 'justify-center',
        )}
      >
        {sort ? (
          <button
            type="button"
            onClick={sort.onSort}
            className="-mx-1 flex items-center gap-1 rounded px-1 hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
          >
            {column.header}
            <SortIcon
              aria-hidden="true"
              className={cn('size-3', !sort.direction && 'opacity-40')}
            />
          </button>
        ) : (
          <span>{column.header}</span>
        )}

        {filter ? (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={filter.label ?? `Filter ${column.header}`}
                data-active={filter.active}
                className={cn(
                  'inline-flex size-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring',
                  filter.active && 'bg-primary text-primary-foreground hover:bg-primary/90',
                )}
              >
                <ListFilter aria-hidden="true" className="size-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              collisionPadding={8}
              className="w-60 max-w-[calc(100vw-1rem)] p-2 normal-case"
            >
              {typeof filter.content === 'function'
                ? filter.content(() => setOpen(false))
                : filter.content}
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
    </TableHead>
  );
}
